import React, { useEffect, useMemo, useState } from "react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { BLOG_POSTS, mapDbPostToUi } from "./blogData";
import { hasSupabaseConfig, supabase } from "../../lib/supabaseClient";

function catClass(cat = "") {
  const k = String(cat).toLowerCase();
  if (k.includes("notic")) return "border-[#CE1126]/40 text-[#f06a78] bg-[#CE1126]/10";
  if (k.includes("gu") || k.includes("competencia")) return "border-[#F5C842]/40 text-[#F5C842] bg-[#F5C842]/10";
  if (k.includes("comunidad") || k.includes("talento")) return "border-emerald-300/40 text-emerald-300 bg-emerald-300/10";
  return "border-cyan-300/40 text-cyan-300 bg-cyan-300/10";
}

const ENABLE_LOCAL_BLOG_FALLBACK = false;

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [sourceLabel, setSourceLabel] = useState("loading");
  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    document.title = "Blog | Puerto Rico Esports League";

    const setMeta = (name, content, isProperty = false) => {
      const selector = isProperty ? `meta[property='${name}']` : `meta[name='${name}']`;
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        if (isProperty) tag.setAttribute("property", name);
        else tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMeta("description", "Noticias, análisis y artículos de Puerto Rico Esports League sobre competencia y comunidad gamer en Puerto Rico.");
    setMeta("og:title", "Blog | Puerto Rico Esports League", true);
    setMeta("og:description", "Contenido oficial de PREL: noticias, guías y análisis del ecosistema esports en Puerto Rico.", true);
    setMeta("og:type", "website", true);
    setMeta("og:url", "https://www.puertoricoesportleague.com/blog", true);
    setMeta("og:image", "https://www.puertoricoesportleague.com/logo.png", true);
    setMeta("twitter:card", "summary_large_image");

    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://www.puertoricoesportleague.com/blog");

    let cancelled = false;

    async function load() {
      if (!hasSupabaseConfig || !supabase) {
        if (cancelled) return;
        if (ENABLE_LOCAL_BLOG_FALLBACK) {
          setPosts(BLOG_POSTS);
          setSourceLabel("fallback");
        } else {
          setPosts([]);
          setSourceLabel("unavailable");
        }
        return;
      }

      const { data, error } = await supabase
        .from("blog_posts")
        .select("slug,title,excerpt,content_md,cover_image_url,category,author_name,published_at,created_at,status")
        .eq("status", "published")
        .order("published_at", { ascending: false });

      if (cancelled) return;

      if (!error && Array.isArray(data) && data.length) {
        setPosts(data.map(mapDbPostToUi));
        setSourceLabel("supabase");
      } else if (ENABLE_LOCAL_BLOG_FALLBACK) {
        setPosts(BLOG_POSTS);
        setSourceLabel("fallback");
      } else {
        setPosts([]);
        setSourceLabel("empty");
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(() => {
    const counts = posts.reduce((acc, p) => {
      const c = p.category || "Noticias";
      acc[c] = (acc[c] || 0) + 1;
      return acc;
    }, {});

    return [
      { key: "all", label: "Todos", count: posts.length },
      ...Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([label, count]) => ({ key: label, label, count })),
    ];
  }, [posts]);

  const filteredPosts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return posts.filter((p) => {
      const byCat = activeCategory === "all" || p.category === activeCategory;
      const bySearch = !q || `${p.title} ${p.excerpt} ${p.category} ${p.author}`.toLowerCase().includes(q);
      return byCat && bySearch;
    });
  }, [posts, activeCategory, search]);

  const featured = filteredPosts[0] || null;
  const gridPosts = filteredPosts.slice(1, visibleCount + 1);
  const progress = filteredPosts.length ? Math.min(((1 + gridPosts.length) / filteredPosts.length) * 100, 100) : 0;

  const trending = useMemo(() => posts.slice(0, 4), [posts]);

  const displaySourceLabel =
    sourceLabel === "supabase"
      ? "Puerto Rico Esports League"
      : sourceLabel === "fallback"
        ? "Puerto Rico Esports League"
        : sourceLabel === "loading"
          ? "Puerto Rico Esports League"
          : "Puerto Rico Esports League";

  return (
    <div className="min-h-screen bg-[#080A10] text-[#F0EEE8]">
      <SiteHeader current="/blog" />

      <section className="relative overflow-hidden border-b border-white/10 px-6 md:px-10 pt-28 md:pt-36 pb-12 md:pb-16">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_80%_at_90%_50%,rgba(0,42,143,0.20)_0%,transparent_65%),radial-gradient(ellipse_40%_60%_at_5%_80%,rgba(206,17,38,0.14)_0%,transparent_60%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />

        <div className="relative z-10 max-w-[1400px] mx-auto">
          <a href="/" className="text-cyan-300 text-sm hover:underline">← Volver al home</a>
          <p className="mt-6 text-xs uppercase tracking-[0.35em] text-[#CE1126]">Puerto Rico Esports League</p>
          <h1 className="mt-3 leading-[0.9] font-black uppercase text-[clamp(2.8rem,8vw,6.5rem)]">
            Noticias <span className="text-transparent [-webkit-text-stroke:1px_rgba(240,238,232,0.24)]">& análisis</span>
          </h1>
          <div className="mt-6 flex items-center gap-4 text-xs uppercase tracking-[0.18em] text-slate-400 flex-wrap">
            <span><strong className="text-white">{posts.length}</strong> publicaciones</span>
            <span className="w-1 h-1 rounded-full bg-slate-600" />
            <span>
              Actualizado{" "}
              <strong className="text-white">
                {new Date().toLocaleDateString("es-PR", { year: "numeric", month: "long", day: "numeric" })}
              </strong>
            </span>
            <span className="w-1 h-1 rounded-full bg-slate-600" />
            <span>Contenido oficial <strong className="text-white">{displaySourceLabel}</strong></span>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-[#0F1220] px-4 md:px-10">
        <div className="max-w-[1400px] mx-auto flex items-center gap-1 overflow-x-auto no-scrollbar">
          {categories.map((c) => (
            <button
              key={c.key}
              onClick={() => {
                setActiveCategory(c.key);
                setVisibleCount(6);
              }}
              className={`px-4 py-4 md:px-6 text-xs uppercase tracking-[0.2em] whitespace-nowrap border-b-2 transition ${
                activeCategory === c.key ? "text-white border-[#CE1126]" : "text-slate-400 border-transparent hover:text-white"
              }`}
              type="button"
            >
              {c.label} <span className="ml-1 text-[10px] text-[#CE1126]">{c.count}</span>
            </button>
          ))}

          <div className="ml-auto min-w-[220px] px-3 md:px-5 py-3 border-l border-white/10">
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setVisibleCount(6);
              }}
              placeholder="Buscar artículos..."
              className="w-full bg-transparent outline-none text-sm text-white placeholder:text-slate-500"
            />
          </div>
        </div>
      </section>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14 grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-10 md:gap-14">
        <div>
          {featured ? (
            <article className="grid lg:grid-cols-2 rounded-2xl overflow-hidden border border-white/10 bg-[#141828]">
              <div className="relative min-h-[280px] md:min-h-[360px]">
                <img src={featured.cover} alt={featured.title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-[#141828]/80" />
                <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.24em] px-3 py-1 bg-[#CE1126] text-white">Destacado</span>
              </div>

              <div className="p-6 md:p-8 flex flex-col justify-center">
                <p className="text-xs uppercase tracking-[0.2em] text-[#F5C842]">{featured.category || "Blog"}</p>
                <h2 className="mt-3 text-3xl md:text-4xl font-black leading-tight uppercase">{featured.title}</h2>
                <p className="mt-4 text-slate-300 leading-relaxed">{featured.excerpt}</p>
                <p className="mt-5 text-xs uppercase tracking-[0.12em] text-slate-500">
                  {featured.author} · {new Date(featured.date).toLocaleDateString()}
                </p>
                <a href={`/blog/${featured.slug}`} className="inline-flex items-center gap-2 mt-5 text-[#CE1126] text-xs uppercase tracking-[0.2em] hover:gap-3 transition-all">
                  Leer artículo →
                </a>
              </div>
            </article>
          ) : (
            <div className="rounded-xl border border-white/10 bg-[#141828] p-6 text-slate-300">No hay publicaciones para este filtro.</div>
          )}

          <div className="mt-8 mb-5 text-xs uppercase tracking-[0.3em] text-slate-500 flex items-center gap-4">
            Últimas publicaciones <span className="h-px flex-1 bg-white/10" />
          </div>

          <div className="grid md:grid-cols-2 gap-2">
            {gridPosts.map((post) => (
              <article key={post.slug} className="group relative bg-[#141828] border border-white/10 overflow-hidden">
                <div className="relative h-44 overflow-hidden">
                  <img src={post.cover} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#141828] to-transparent" />
                  <span className={`absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border ${catClass(post.category)}`}>
                    {post.category}
                  </span>
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="text-lg font-bold uppercase leading-snug">{post.title}</h3>
                  <p className="text-sm text-slate-300 mt-2 line-clamp-2">{post.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between text-[11px] uppercase tracking-[0.12em] text-slate-500">
                    <span>{new Date(post.date).toLocaleDateString()}</span>
                    <a href={`/blog/${post.slug}`} className="text-[#CE1126] hover:underline">Leer</a>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#CE1126] scale-x-0 origin-left transition-transform duration-300 group-hover:scale-x-100" />
              </article>
            ))}
          </div>

          {!!filteredPosts.length && filteredPosts.length > 1 ? (
            <div className="mt-8 flex items-center gap-4">
              <button
                type="button"
                onClick={() => setVisibleCount((n) => Math.min(n + 6, Math.max(filteredPosts.length - 1, 0)))}
                className="px-5 py-3 border border-white/20 uppercase tracking-[0.2em] text-xs hover:border-[#CE1126] hover:bg-[#CE1126]/10 transition"
                disabled={1 + gridPosts.length >= filteredPosts.length}
              >
                Cargar más
              </button>
              <div className="h-[2px] bg-white/10 flex-1 rounded overflow-hidden">
                <div className="h-full bg-[#CE1126]" style={{ width: `${progress}%` }} />
              </div>
              <span className="text-xs uppercase tracking-[0.12em] text-slate-500 whitespace-nowrap">
                {Math.min(1 + gridPosts.length, filteredPosts.length)} de {filteredPosts.length}
              </span>
            </div>
          ) : null}
        </div>

        <aside className="xl:sticky xl:top-24 h-fit space-y-6">
          <section className="border border-white/10 bg-[#141828] p-5 md:p-6 border-t-2 border-t-[#CE1126]">
            <h3 className="text-xl font-bold uppercase">Lo más reciente</h3>
            <div className="mt-3 space-y-2">
              {trending.map((p, idx) => (
                <a key={p.slug} href={`/blog/${p.slug}`} className="block border-b border-white/10 py-3 last:border-b-0 group">
                  <p className="text-xs text-[#CE1126] font-black">{String(idx + 1).padStart(2, "0")}</p>
                  <p className="text-sm uppercase leading-snug group-hover:text-white text-slate-300">{p.title}</p>
                </a>
              ))}
            </div>
          </section>

          <section className="border border-white/10 bg-[#141828] p-5 md:p-6">
            <h3 className="text-xl font-bold uppercase">Categorías</h3>
            <div className="mt-3 space-y-2">
              {categories.filter((c) => c.key !== "all").map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setActiveCategory(c.key)}
                  className="w-full flex items-center justify-between text-left px-3 py-2 border border-white/10 hover:border-white/30 transition"
                >
                  <span className="text-xs uppercase tracking-[0.16em] text-slate-300">{c.label}</span>
                  <span className="text-sm text-slate-500">{c.count}</span>
                </button>
              ))}
            </div>
          </section>
        </aside>
      </main>

      <SiteFooter />
    </div>
  );
}
